import { useCallback, useMemo } from "react";
import { DataTable, Pagination } from "@/components/molecules";
import { useOperator, usePagination, useSorting } from "@/hooks";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { Page } from "@/redux/types";
import { affiliateVehiclesColumns } from "./fixtures";
import { generateAffiliateVehicleTableData } from "./helpers";
import { useUpdateAffiliateVehicleMutation } from "@/redux/apis/vehicleGuest/vehicleGuestApi";
import { addToast } from "@/utils";
import { useAppDispatch } from "@/redux/hooks";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { vehicleApi } from "@/redux/apis/vehicle/vehicleApi";
import { Loading } from "@/components/atoms";

export interface AffiliateVehiclesTableProps {
  items: Vehicle[];
  setPendingArchive: (vehicle: Vehicle) => void;
}

export const AffiliateVehiclesTable = ({ items, setPendingArchive }: AffiliateVehiclesTableProps) => {
  const dispatch = useAppDispatch();
  const { id: operatorId } = useOperator();
  const { sorting, setSorting } = useSorting();
  const [updateAffiliateVehicle, { isLoading }] = useUpdateAffiliateVehicleMutation();

  const onArchiveClick = useCallback(
    (vehicle: Vehicle) => {
      setPendingArchive(vehicle);
    },
    [setPendingArchive]
  );

  const handleApprove = (vehicle: Vehicle, isReapprove?: boolean) => {
    updateAffiliateVehicle({
      ownerId: operatorId,
      vehicleId: vehicle.uuid,
      approval_status: "APPROVED",
    })
      .unwrap()
      .then(() => {
        if (isReapprove) addToast("success", "Vehicle reapproved successfully");
        else addToast("success", "Vehicle approved successfully");
        dispatch(vehicleApi.util.invalidateTags(["Vehicle"]));
      })
      .catch((e) => getErrorMessages(e).forEach((message) => addToast("danger", message)));
  };

  const handleDeny = (vehicle: Vehicle) => {
    updateAffiliateVehicle({
      ownerId: operatorId,
      vehicleId: vehicle.uuid,
      approval_status: "DENIED",
      approval_reason: "Vehicle denied by operator",
    })
      .unwrap()
      .then(() => {
        addToast("success", "Vehicle denied successfully");
        dispatch(vehicleApi.util.invalidateTags(["Vehicle"]));
      })
      .catch((e) => getErrorMessages(e).forEach((message) => addToast("danger", message)));
  };

  const data = useMemo(() => 
    generateAffiliateVehicleTableData(operatorId, items, handleApprove, handleDeny, onArchiveClick),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [items, operatorId]);

  return (
    <div className="relative">
      {isLoading && <Loading className="rounded-lg" />}
      <DataTable
        className="w-full overflow-x-auto [&_table]:table-auto [&_tr]:bg-white"
        data={data}
        columns={affiliateVehiclesColumns}
        sorting={sorting}
        setSorting={setSorting}
      />
    </div>
  );
};

const AffiliateVehiclesTablePagination = ({ page }: { page: Page }) => {
  const { current, size, total, count } = page;
  const { handlePageClick, handleSizeChange } = usePagination("affiliate-vehicles", 30);

  return (
    <Pagination
      onPageClick={handlePageClick}
      onSizeChange={handleSizeChange}
      currentPage={current}
      pageSize={size}
      totalItems={total}
      pageCount={count}
    />
  );
};

AffiliateVehiclesTable.Pagination = AffiliateVehiclesTablePagination;
